// features/sa-lab/api/streamTurno.js
//
// POST /lab/sessioni/{id}/turno risponde text/event-stream: EventSource non
// supporta POST né header Authorization, quindi si legge lo stream a mano
// con fetch + response.body.getReader() e un piccolo parser SSE.
//
// Base URL e token sono gli stessi del client KM (api/client.js): la base
// viene da KM_CONFIG.API_BASE_URL, il JWT dal vero AuthContext. Errori HTTP
// mappati con lo stesso errorMapper del resto del KM, così ErrorDetails li
// mostra uguali alle altre pagine.

import { KM_CONFIG } from '../../../api/config.js';
import { mapResponseError, mapNetworkError } from '../../../api/errorMapper.js';

/**
 * Divide il buffer in blocchi SSE completi (separati da riga vuota) e
 * restituisce { eventi, resto }: il resto è il blocco incompleto da tenere
 * per la lettura successiva.
 */
export function parseBloccoSse(buffer) {
  const normalizzato = buffer.replace(/\r\n?/g, '\n');
  const blocchi = normalizzato.split('\n\n');
  const resto = blocchi.pop(); // ultimo pezzo: potrebbe essere a metà
  const eventi = [];
  blocchi.forEach((blocco) => {
    if (!blocco.trim()) return;
    let nome = 'message';
    const dati = [];
    blocco.split('\n').forEach((riga) => {
      if (riga.startsWith(':')) return; // commento / keep-alive
      const i = riga.indexOf(':');
      const campo = i === -1 ? riga : riga.slice(0, i);
      let valore = i === -1 ? '' : riga.slice(i + 1);
      if (valore.startsWith(' ')) valore = valore.slice(1);
      if (campo === 'event') nome = valore;
      else if (campo === 'data') dati.push(valore);
    });
    if (!dati.length) return;
    const grezzo = dati.join('\n');
    let data;
    try {
      data = JSON.parse(grezzo);
    } catch {
      data = { grezzo };
    }
    eventi.push({ nome, data });
  });
  return { eventi, resto };
}

/**
 * Esegue un turno in streaming.
 * @param {object} opts
 * @param {string} opts.sessionId
 * @param {object} opts.body - {} per l'apertura, { testo } o { scelta }
 * @param {() => string|null} opts.getToken
 * @param {() => void} [opts.onUnauthorized]
 * @param {(nome: string, data: any) => void} opts.onEvento
 * @param {AbortSignal} [opts.signal]
 */
export async function streamTurno({ sessionId, body, getToken, onUnauthorized, onEvento, signal }) {
  const url = `${KM_CONFIG.API_BASE_URL.replace(/\/$/, '')}/lab/sessioni/${encodeURIComponent(sessionId)}/turno`;
  const headers = { 'Content-Type': 'application/json', Accept: 'text/event-stream' };
  const token = getToken?.();
  if (token) headers.Authorization = `Bearer ${token}`;

  // Timeout lungo come le altre operazioni LLM; propaga anche l'abort esterno
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), KM_CONFIG.TIMEOUT_LLM_MS);
  if (signal) signal.addEventListener('abort', () => controller.abort(), { once: true });

  let response;
  try {
    response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body || {}),
      signal: controller.signal,
    });
  } catch (err) {
    clearTimeout(timeoutId);
    if (signal?.aborted) throw err;
    throw mapNetworkError(err, { timedOut: controller.signal.aborted });
  }

  if (!response.ok) {
    clearTimeout(timeoutId);
    if (response.status === 401) onUnauthorized?.();
    throw await mapResponseError(response);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  try {
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const { eventi, resto } = parseBloccoSse(buffer);
      buffer = resto;
      eventi.forEach((e) => onEvento(e.nome, e.data));
    }
    // Ultimo blocco senza riga vuota finale
    buffer += decoder.decode();
    if (buffer.trim()) {
      const { eventi } = parseBloccoSse(`${buffer}\n\n`);
      eventi.forEach((e) => onEvento(e.nome, e.data));
    }
  } catch (err) {
    if (signal?.aborted) throw err;
    throw mapNetworkError(err, { timedOut: controller.signal.aborted });
  } finally {
    clearTimeout(timeoutId);
  }
}
